import { QueryClient } from "@tanstack/react-query";
import { authManager } from "../data/managers/AuthManager";
import { HttpMethodEnum } from "../data/models/HttpMethodEnum";

export interface ApiResponse<T> {
  data: T | undefined;
  isSuccess: boolean;
  messageError?: string;
}

export const PROD_API_BASE_URL =
  "https://api-server-9110340501.us-central1.run.app";
export const LOCAL_API_BASE_URL = "http://localhost:8080";
export const DEV_API_BASE_URL =
  "https://api-server-dev-9110340501.us-central1.run.app";
export const API_BASE_URL_OVERRIDE_KEY = "lypro_api_base_url_override";

const normalizeApiBaseUrl = (value: string) => value.trim().replace(/\/$/, "");

const isLocalHostname = (hostname: string) =>
  hostname === "localhost" || hostname === "127.0.0.1";

const isDevHostname = (hostname: string) =>
  /(^|[.-])dev([.-]|$)/i.test(hostname);

export const getApiBaseUrlOverride = () => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(API_BASE_URL_OVERRIDE_KEY)?.trim() || "";
};

export const setApiBaseUrlOverride = (value: string) => {
  if (typeof window === "undefined") return;
  const normalizedValue = normalizeApiBaseUrl(value);
  if (!normalizedValue) {
    window.localStorage.removeItem(API_BASE_URL_OVERRIDE_KEY);
    return;
  }
  window.localStorage.setItem(API_BASE_URL_OVERRIDE_KEY, normalizedValue);
};

export const clearApiBaseUrlOverride = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(API_BASE_URL_OVERRIDE_KEY);
};

export const getDefaultApiBaseUrl = () => {
  if (typeof window === "undefined") return LOCAL_API_BASE_URL;

  const hostname = window.location.hostname.trim().toLowerCase();

  if (!hostname || isLocalHostname(hostname)) {
    return LOCAL_API_BASE_URL;
  }

  if (isDevHostname(hostname)) {
    return DEV_API_BASE_URL;
  }

  return PROD_API_BASE_URL;
};

export const getConfiguredApiBaseUrl = () =>
  normalizeApiBaseUrl(
    getDefaultApiBaseUrl() === LOCAL_API_BASE_URL
      ? LOCAL_API_BASE_URL
      : getApiBaseUrlOverride() ||
        process.env.REACT_APP_API_BASE_URL ||
        getDefaultApiBaseUrl()
  );

export class RequestBuilder {
  private queryClient: QueryClient;

  constructor() {
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: { staleTime: 0 },
      },
    });
  }

  private generateKey(
    method: HttpMethodEnum,
    url: string,
    params?: any
  ): string {
    return `${method}:${url}${params ? JSON.stringify(params) : ""}`;
  }

  private buildUrl(url: string, method: HttpMethodEnum, params?: any): string {
    const baseUrl = getConfiguredApiBaseUrl();
    if (method === HttpMethodEnum.GET && params) {
      const queryString = new URLSearchParams(params).toString();
      return `${baseUrl}${url}?${queryString}`;
    }
    return `${baseUrl}${url}`;
  }

  private buildNonJsonMessage(
    requestUrl: string,
    contentType: string,
    responseText: string
  ): string {
    const trimmedResponse = responseText.trim();

    if (trimmedResponse.startsWith("<")) {
      return `El endpoint ${requestUrl} devolvio HTML en lugar de JSON. Revisa la URL base del API o si esa ruta ya esta desplegada.`;
    }

    if (!trimmedResponse) {
      return `El endpoint ${requestUrl} devolvio una respuesta vacia.`;
    }

    return `El endpoint ${requestUrl} devolvio una respuesta no JSON${
      contentType ? ` (${contentType})` : ""
    }.`;
  }

  private tryParseJson<T>(
    requestUrl: string,
    contentType: string,
    responseText: string
  ): { ok: true; data: T } | { ok: false; messageError: string } {
    const trimmedResponse = responseText.trim();

    if (!trimmedResponse) {
      return { ok: true, data: undefined as T };
    }

    const looksLikeJson =
      contentType.includes("application/json") ||
      trimmedResponse.startsWith("{") ||
      trimmedResponse.startsWith("[");

    if (!looksLikeJson) {
      return {
        ok: false,
        messageError: this.buildNonJsonMessage(
          requestUrl,
          contentType,
          responseText
        ),
      };
    }

    try {
      return {
        ok: true,
        data: JSON.parse(trimmedResponse) as T,
      };
    } catch {
      return {
        ok: false,
        messageError: this.buildNonJsonMessage(
          requestUrl,
          contentType,
          responseText
        ),
      };
    }
  }

  private async request<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any
  ): Promise<ApiResponse<T>> {
    const requestUrl = this.buildUrl(url, method, params);
    const authToken = await authManager.getAuthToken();
    const hasJsonBody =
      method !== HttpMethodEnum.GET && params !== null && params !== undefined;
    const options: RequestInit = {
      method,
      // Keep the browser HTTP cache from returning an old GET after a mutation.
      // RequestBuilder's QueryClient is the only cache this client should use.
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: hasJsonBody ? JSON.stringify(params) : undefined,
    };
    try {
      const response = await fetch(requestUrl, options);
      const contentType = response.headers.get("content-type") || "";
      const responseText = await response.text();
      const parsedResponse = this.tryParseJson<{
        data?: T;
        messageError?: string;
      }>(requestUrl, contentType, responseText);

      if (!parsedResponse.ok) {
        return {
          data: undefined,
          isSuccess: false,
          messageError: parsedResponse.messageError,
        };
      }

      const responseData = parsedResponse.data;
      if (!response.ok) {
        const serverError =
          typeof responseData?.data === "string"
            ? responseData.data
            : responseData?.messageError;
        return {
          data: undefined,
          isSuccess: false,
          messageError:
            serverError || `Error ${response.status}: ${response.statusText}`,
        };
      }
      return {
        data: responseData.data,
        isSuccess: true,
      };
    } catch (error: any) {
      console.error("Fetch error:", error);
      return {
        data: undefined,
        isSuccess: false,
        messageError: error.message || "Unknown error",
      };
    }
  }

  async requestWithCache<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any,
    staleTime = 0
  ): Promise<ApiResponse<T>> {
    const queryKey = [this.generateKey(method, url, params)];
    return this.queryClient.fetchQuery<ApiResponse<T>>({
      queryKey,
      queryFn: () => this.request<T>(method, url, params),
      staleTime,
    });
  }

  async requestHard<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any
  ): Promise<ApiResponse<T>> {
    const queryKey = this.generateKey(method, url, params);
    await this.queryClient.invalidateQueries({
      queryKey,
      refetchType: "active",
    });
    return this.request<T>(method, url, params);
  }

  createEndpoint<T>(
    method: HttpMethodEnum,
    url: string
  ): {
    request: (params?: any, staleTime?: number) => Promise<ApiResponse<T>>;
    requestHard: (params?: any) => Promise<ApiResponse<T>>;
  } {
    return {
      request: (params?: any, staleTime?: number): Promise<ApiResponse<T>> =>
        this.requestWithCache<T>(method, url, params, staleTime),
      requestHard: (params?: any): Promise<ApiResponse<T>> =>
        this.requestHard<T>(method, url, params),
    };
  }
}
